import {
  Send,
  Smile,
  Paperclip,
  Hash,
  MessageSquare,
  Users,
  Trash2,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import { Badge } from "../components/ui/badge";
import { ScrollArea } from "../components/ui/scroll-area";
import { useParams, useSearchParams } from "react-router-dom";
import { useState, useEffect, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import ZulipServices from "../services/APIs/zulipServices";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../components/ui/popover";
import BackButton from "../components/BackButton";
import CitycollabNavigationRoutes from "../utils/navigationRoutes";

const apiBaseUrl = import.meta.env.VITE_CITYCOLLAB_API_ROOT_URL;

// Helper function to format Zulip message content
const formatMessageContent = (html: any) => {
  if (!html) return "";
  const tempDiv = document.createElement("div");
  tempDiv.innerHTML = html;

  // Get auth credentials from token
  const authToken = localStorage.getItem("zulipToken");
  let authParams = "";
  if (authToken && authToken.startsWith("Basic ")) {
    try {
      const decoded = atob(authToken.replace("Basic ", ""));
      const [email, apiKey] = decoded.split(":");
      authParams = `api_key=${apiKey}&email=${encodeURIComponent(email)}`;
    } catch (e) {
      console.error("Error decoding zulipToken", e);
    }
  }

  // Image file extensions — render as preview
  const IMAGE_EXTS = /\.(png|jpe?g|gif|webp|svg)$/i;
  // Non-image file extensions — render as a download link
  const FILE_EXTS = /\.(pdf|docx?|xlsx?|csv|txt|pptx?|zip|rar|7z|mp4|mov|avi|mp3|wav)$/i;

  // Fix relative URLs for user uploads
  const links = tempDiv.querySelectorAll("a");
  links.forEach(link => {
    const href = link.getAttribute("href");
    if (href && href.startsWith("/")) {
      const fullUrl = `${apiBaseUrl}${href}`;
      const separator = fullUrl.includes("?") ? "&" : "?";
      link.setAttribute("href", authParams ? `${fullUrl}${separator}${authParams}` : fullUrl);
      link.setAttribute("target", "_blank");
      link.setAttribute("rel", "noopener noreferrer");
    }

    const linkText = link.textContent?.trim() ?? "";
    const isUploadLink =
      href?.includes("/user_uploads/") || href?.startsWith("/");

    if (!isUploadLink) return;

    const containsImg = link.querySelector("img") !== null;
    if (containsImg) {
      // Anchor wraps an image — unwrap so the <img> still renders as preview
      while (link.firstChild) {
        link.parentNode?.insertBefore(link.firstChild, link);
      }
      link.remove();
    } else if (IMAGE_EXTS.test(linkText)) {
      // Plain image filename link — remove (image block handles it)
      link.remove();
    } else if (FILE_EXTS.test(linkText)) {
      // Non-image file — style it as a download link chip
      link.style.cssText = [
        "display:inline-flex",
        "align-items:center",
        "gap:6px",
        "padding:4px 10px",
        "border-radius:6px",
        "border:1px solid currentColor",
        "opacity:0.75",
        "font-size:0.8rem",
        "font-weight:500",
        "text-decoration:none",
        "word-break:break-all",
      ].join(";");
      if (!link.querySelector(".file-link-icon")) {
        const icon = document.createElement("span");
        icon.className = "file-link-icon";
        icon.textContent = "📎";
        link.prepend(icon);
      }
    }
  });

  const images = tempDiv.querySelectorAll("img");
  images.forEach(img => {
    const src = img.getAttribute("src");
    if (src && (src.startsWith("/") || src.includes("/user_uploads/"))) {
      const fullUrl = src.startsWith("/") ? `${apiBaseUrl}${src}` : src;
      const separator = fullUrl.includes("?") ? "&" : "?";
      img.setAttribute("src", authParams ? `${fullUrl}${separator}${authParams}` : fullUrl);
    }
  });

  return tempDiv.innerHTML;
};

// Common Zulip emoji mapping (Expanded for better variety)
const emojiMap: Record<string, string> = {
  // Popular/Smileys
  smile: "😊",
  laughing: "😆",
  wink: "😉",
  heart_eyes: "😍",
  sweat_smile: "😅",
  joy: "😂",
  // People/Emotions
  thumbs_up: "👍",
  thumbs_down: "👎",
  ok_hand: "👌",
  pray: "🙏",
  heart: "❤️",
  broken_heart: "💔",
  // Nature/Objects
  fire: "🔥",
  rocket: "🚀",
  tada: "🎉",
  party_popper: "🥳",
  100: "💯",
  sparkles: "✨",
  star: "⭐",
  eyes: "👀",
  octopus: "🐙",
  checkmark: "✅",
  hammer_and_wrench: "🛠️",
  construction: "🏗️",
};

const emojiCategories = [
  { label: "Popular", emojis: ["smile", "laughing", "heart", "thumbs_up", "tada", "fire", "octopus", "hammer_and_wrench"] },
  { label: "Smileys & Emotion", emojis: ["wink", "heart_eyes", "sweat_smile", "joy", "broken_heart", "eyes"] },
  { label: "Hands & Body", emojis: ["thumbs_down", "ok_hand", "pray", "checkmark", "100"] },
  { label: "Objects & More", emojis: ["rocket", "party_popper", "sparkles", "star", "construction"] },
];

const emojiGlyph = (text: string) => (
  <span className="emoji-font inline-flex min-h-[1.125rem] min-w-[1.125rem] items-center justify-center text-[1.125rem]">
    {text}
  </span>
);

const getEmojiDisplay = (reaction: any, customEmojis: Record<string, any> = {}) => {
  if (reaction.reaction_type === "unicode_emoji") {
    try {
      return emojiGlyph(String.fromCodePoint(parseInt(reaction.emoji_code, 16)));
    } catch {
      return emojiGlyph(emojiMap[reaction.emoji_name] || `:${reaction.emoji_name}:`);
    }
  }
  if (reaction.reaction_type === "realm_emoji" || reaction.reaction_type === "zulip_extra_emoji") {
    const custom = customEmojis[reaction.emoji_name] || customEmojis[reaction.emoji_code];
    if (custom) {
      return <img src={custom.source_url} alt={reaction.emoji_name} className="h-4 w-4 object-contain" />;
    }
  }
  return emojiGlyph(emojiMap[reaction.emoji_name] || `:${reaction.emoji_name}:`);
};

// Helper function to format message time (12-hour format with AM/PM)
const formatMessageTime = (timestamp: any) => {
  const date = new Date(timestamp * 1000);
  return date.toLocaleTimeString("en-US", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
};

// Helper function to format time ago
const formatTimeAgo = (timestamp: any) => {
  const now = Date.now();
  const diff = now - timestamp * 1000;
  const hours = Math.floor(diff / (1000 * 60 * 60));
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));

  if (hours < 1) {
    return "just now";
  }
  if (hours < 24) {
    return `${hours} hour${hours > 1 ? "s" : ""} ago`;
  }
  if (days < 7) {
    return `${days} day${days > 1 ? "s" : ""} ago`;
  }
  return new Date(timestamp * 1000).toLocaleDateString();
};

const ChannelDetail = () => {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const channelId = searchParams.get("channelId") ?? id;
  const [isSubscribed, setIsSubscribed] = useState(false);

  // Fetch custom emojis
  const { data: customEmojisData } = useQuery({
    queryKey: ["customEmojis"],
    queryFn: () => ZulipServices.getCustomEmojis(),
  });

  const customEmojis = customEmojisData?.emoji || {};
  const [message, setMessage] = useState("");
  const [selectedTopic, setSelectedTopic] = useState<string | null>(null);
  const [messages, setMessages] = useState<any[]>([]);
  const [channelList, setChannelList] = useState<any[]>([]);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [messageToDeleteId, setMessageToDeleteId] = useState<number | null>(null);
  const currentUserId = parseInt(
    localStorage.getItem("zulipUserId") ?? "0",
    10
  );

  // Fetch users data
  const { data: usersData } = useQuery({
    queryKey: ["/users"],
    queryFn: () => ZulipServices.getUsers(),
  });

  // Fetch channel list
  const { data: channelsData } = useQuery({
    queryKey: ["zulip", "streams"],
    queryFn: async () => {
      const res = await ZulipServices.getChannelList();
      return res?.streams ?? [];
    },
  });

  // Fetch channel members for accurate member count
  const { data: usersDataByStreams } = useQuery({
    queryKey: ["/streams", channelId],
    queryFn: () => ZulipServices.getUsersByChannel(channelId ?? ""),
    enabled: !!channelId,
  });

  useEffect(() => {
    if (channelsData?.length) {
      setChannelList(channelsData);
    }
  }, [channelsData]);

  // Get channel info - Dynamic
  const channel = useMemo(() => {
    if (!channelId || !channelList.length) {
      return {
        id: id ?? "1",
        name: "Loading...",
        description: "",
        memberCount: 0,
      };
    }

    const foundChannel = channelList.find(
      (c: any) =>
        c.stream_id === Number(channelId) || c.id === Number(channelId)
    );

    if (foundChannel) {
      // Get member count from subscribers if available
      const memberCount =
        usersDataByStreams?.subscribers?.length ??
        foundChannel.subscriber_count ??
        0;

      return {
        id: foundChannel.stream_id ?? foundChannel.id,
        name: foundChannel.name ?? "Unknown Channel",
        description: foundChannel.description ?? "",
        memberCount: memberCount,
      };
    }

    return {
      id: channelId,
      name: "Unknown Channel",
      description: "",
      memberCount: 0,
    };
  }, [channelId, channelList, id, usersDataByStreams]);

  // GET Chat of Channel - Same as ChatPage
  const chatOfChannelPayload = useMemo(
    () => ({
      anchor: "newest",
      num_before: 100,
      num_after: 0,
      narrow: JSON.stringify([
        { negated: false, operator: "channel", operand: Number(channelId) },
      ]),
      client_gravatar: true,
      allow_empty_topic_name: true,
    }),
    [channelId]
  );

  const { data: channelChatData, refetch: refetchChannelChatData } = useQuery({
    queryKey: ["messages", chatOfChannelPayload],
    queryFn: () =>
      ZulipServices.getChatOfChannel({ chatOfChannelPayload } as any),
    enabled: !!channelId,
    refetchInterval: 5000,
    refetchOnWindowFocus: true,
  });

  useEffect(() => {
    if (channelChatData?.messages && Array.isArray(channelChatData.messages)) {
      // Sort messages by timestamp (oldest first for display)
      const sortedMessages = [...channelChatData.messages].sort(
        (a: any, b: any) => a.timestamp - b.timestamp
      );
      setMessages(sortedMessages);
    } else {
      setMessages([]);
    }
  }, [channelChatData]);

  // Extract unique topics from messages - Dynamic
  const topics = useMemo(() => {
    if (!messages.length) {
      return [];
    }

    const topicMap = new Map();
    messages.forEach((msg: any) => {
      const topic = msg.subject ?? "general";
      if (!topicMap.has(topic)) {
        topicMap.set(topic, {
          id: topic,
          title: topic,
          messageCount: 0,
          lastActivity: msg.timestamp,
          unread: 0,
        });
      }
      const topicData = topicMap.get(topic);
      topicData.messageCount++;
      if (msg.timestamp > topicData.lastActivity) {
        topicData.lastActivity = msg.timestamp;
      }
    });

    return Array.from(topicMap.values())
      .sort((a, b) => b.lastActivity - a.lastActivity)
      .map(topic => ({
        ...topic,
        lastActivity: formatTimeAgo(topic.lastActivity),
      }));
  }, [messages]);

  // Filter messages by selected topic
  const filteredMessages = useMemo(() => {
    if (!selectedTopic) {
      // When no topic selected, show all messages
      return messages;
    }
    // Filter messages by selected topic
    return messages.filter((msg: any) => {
      const msgTopic = msg.subject ?? "general";
      return msgTopic === selectedTopic;
    });
  }, [messages, selectedTopic]);

  const handleSendMessage = async () => {
    if (!message.trim() || !channelId) {
      return;
    }

    try {
      const payload = {
        to: [Number(channelId)],
        type: "stream",
        topic: selectedTopic ?? "channel events",
        content: message,
      };
      await ZulipServices.sendChannelMessage(payload);
      setMessage("");
      refetchChannelChatData?.();
    } catch (error) {
      console.error("Error sending message:", error);
    }
  };

  const handleDeleteMessage = (messageId: number) => {
    setMessageToDeleteId(messageId);
    setIsDeleteDialogOpen(true);
  };

  const confirmDeleteMessage = async () => {
    if (messageToDeleteId === null) return;
    try {
      await ZulipServices.deleteMessage(messageToDeleteId);
      if (refetchChannelChatData) {
        refetchChannelChatData();
      }
    } catch (error) {
      console.error("Error deleting message:", error);
    } finally {
      setIsDeleteDialogOpen(false);
      setMessageToDeleteId(null);
    }
  };

  const handleToggleReaction = async (messageId: number, emojiName: string, hasReacted: boolean) => {
    try {
      if (hasReacted) {
        await ZulipServices.removeReaction(messageId, emojiName);
      } else {
        await ZulipServices.addReaction(messageId, emojiName);
      }
      refetchChannelChatData?.();
    } catch (error) {
      console.error("Error toggling reaction:", error);
    }
  };

  return (
    <div className="flex min-h-screen flex-col bg-background">
      <header className="sticky top-0 z-50 border-b border-border bg-card shadow-sm">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center gap-3">
            <BackButton fallback={CitycollabNavigationRoutes.NEW_DESIGN} />
            <div className="flex flex-1 items-center gap-2">
              <Hash className="h-5 w-5 text-primary" />
              <div>
                <h1 className="text-lg font-bold text-primary">
                  {channel.name}
                </h1>
                <p className="text-xs text-muted-foreground">
                  {channel.description}
                </p>
              </div>
            </div>
            <Button variant="outline" size="sm">
              <Users className="mr-2 h-4 w-4" />
              {channel.memberCount}
            </Button>
          </div>
        </div>
      </header>

      <div className="flex flex-1 overflow-hidden">
        {/* Topics Sidebar */}
        <aside className="hidden w-80 border-r border-border bg-card md:block">
          <div className="border-b border-border p-4">
            <h2 className="flex items-center gap-2 text-sm font-semibold">
              <MessageSquare className="h-4 w-4" />
              Topics
            </h2>
          </div>
          <ScrollArea className="h-[calc(100vh-12rem)]">
            <div className="space-y-1 p-2">
              {topics.length === 0 ? (
                <div className="p-3 text-center text-sm text-muted-foreground">
                  No topics yet
                </div>
              ) : (
                topics.map(topic => (
                  <button
                    key={topic.id}
                    onClick={() =>
                      setSelectedTopic(
                        topic.id === selectedTopic ? null : topic.id
                      )
                    }
                    className={`w-full rounded-lg p-3 text-left transition-colors ${selectedTopic === topic.id
                      ? "border border-primary/20 bg-primary/10"
                      : "hover:bg-muted"
                      }`}
                  >
                    <div className="mb-1 flex items-start justify-between">
                      <h3 className="line-clamp-1 text-sm font-medium">
                        {topic.title}
                      </h3>
                      {topic.unread > 0 && (
                        <Badge
                          variant="default"
                          className="h-5 min-w-5 px-1.5 text-xs"
                        >
                          {topic.unread}
                        </Badge>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {topic.messageCount} messages · {topic.lastActivity}
                    </p>
                  </button>
                ))
              )}
            </div>
          </ScrollArea>
        </aside>

        {/* Messages Area */}
        <main className="flex flex-1 flex-col">
          <div className="border-b border-border bg-muted/30 p-4">
            <h3 className="text-sm font-semibold">
              {selectedTopic
                ? (topics.find(t => t.id === selectedTopic)?.title ??
                  "All Messages")
                : "All Messages"}
            </h3>
          </div>

          <ScrollArea className="flex-1 p-4">
            <div className="space-y-4">
              {!messages.length ? (
                <div className="py-8 text-center text-sm text-muted-foreground">
                  Loading messages...
                </div>
              ) : filteredMessages.length === 0 ? (
                <div className="py-8 text-center text-sm text-muted-foreground">
                  No messages in this topic. Start the conversation!
                </div>
              ) : (
                filteredMessages.map((msg: any) => {
                  const user =
                    usersData?.members?.find(
                      (u: any) => u.user_id === msg.sender_id
                    ) ?? null;

                  const avatarUrl = user?.avatar_url?.startsWith("http")
                    ? user.avatar_url
                    : user?.avatar_url
                      ? `${apiBaseUrl}${user.avatar_url}`
                      : null;

                  return (
                    <Card key={msg.id} className="group bg-card">
                      <CardContent className="p-4 pt-5">
                        <div className="flex items-start gap-3">
                          <Avatar className="h-10 w-10 shrink-0">
                            <AvatarImage src={avatarUrl ?? undefined} />
                            <AvatarFallback>
                              {user?.full_name
                                ?.split(" ")
                                .map((n: string) => n[0])
                                .join("")
                                .slice(0, 2)
                                .toUpperCase() ?? "U"}
                            </AvatarFallback>
                          </Avatar>
                          <div className="min-w-0 flex-1">
                            <div className="mb-1 flex items-baseline gap-2">
                              <span className="text-sm font-semibold">
                                {user?.full_name ?? "Unknown User"}
                              </span>
                                <span className="text-xs text-muted-foreground">
                                  {formatMessageTime(msg.timestamp)}
                                </span>
                                {msg.sender_id === currentUserId && (
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    onClick={() => handleDeleteMessage(msg.id)}
                                    className="h-6 w-6 ml-auto text-muted-foreground hover:text-destructive"
                                  >
                                    <Trash2 className="h-3 w-3" />
                                  </Button>
                                )}
                              </div>
                            <div
                              className="zulip-message-content text-sm text-foreground"
                              dangerouslySetInnerHTML={{
                                __html: formatMessageContent(msg.content),
                              }}
                            />

                            {/* Reactions Row */}
                            {msg.reactions && msg.reactions.length > 0 && (
                              <div className="mt-3 flex flex-wrap items-center gap-1.5 border-t border-border pt-2">
                                {Object.values(
                                  msg.reactions.reduce((acc: any, r: any) => {
                                    const key = r.emoji_name;
                                    if (!acc[key]) {
                                      acc[key] = { ...r, count: 0, hasReacted: false };
                                    }
                                    acc[key].count++;
                                    if (r.user_id === currentUserId) acc[key].hasReacted = true;
                                    return acc;
                                  }, {})
                                ).map((reaction: any) => (
                                  <Badge
                                    key={`${msg.id}-${reaction.emoji_name}`}
                                    variant="outline"
                                    className={`flex h-7 cursor-pointer items-center gap-1.5 px-2 rounded-full transition-colors ${reaction.hasReacted ? "border-primary bg-primary/10 text-primary" : "border-border text-muted-foreground hover:bg-muted"}`}
                                    onClick={() => handleToggleReaction(msg.id, reaction.emoji_name, reaction.hasReacted)}
                                  >
                                    <span className="inline-flex shrink-0 items-center justify-center">
                                      {getEmojiDisplay(reaction, customEmojis)}
                                    </span>
                                    {reaction.hasReacted && (
                                      <span className="text-[10px] font-semibold">You</span>
                                    )}
                                    {(!reaction.hasReacted || reaction.count > 1) && (
                                      <span className="text-[11px] font-medium opacity-70">
                                        {reaction.hasReacted ? reaction.count - 1 || "" : reaction.count}
                                      </span>
                                    )}
                                  </Badge>
                                ))}
                              </div>
                            )}

                            <div className="mt-2 flex items-center justify-between gap-2">
                              <div className="flex items-center gap-2">
                                <span className="text-xs text-muted-foreground">
                                  {formatMessageTime(msg.timestamp)}
                                </span>
                                {msg.sender_id === currentUserId && (
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    onClick={() => handleDeleteMessage(msg.id)}
                                    className="h-6 w-6 ml-auto text-muted-foreground hover:text-destructive"
                                  >
                                    <Trash2 className="h-3 w-3" />
                                  </Button>
                                )}
                              </div>

                              {/* Add reaction picker */}
                              <Popover>
                                <PopoverTrigger asChild>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className={`h-6 w-6 rounded-full transition-opacity ${msg.reactions?.length ? "opacity-100" : "opacity-0 group-hover:opacity-100"} text-muted-foreground hover:bg-muted hover:text-foreground`}
                                  >
                                    <Smile className="h-3.5 w-3.5" />
                                  </Button>
                                </PopoverTrigger>
                                <PopoverContent className="w-auto p-1" side="top" align="start">
                                  <div className="flex flex-col max-h-80 overflow-y-auto">
                                    {/* Standard Emojis */}
                                    {emojiCategories.map((category) => (
                                      <div key={category.label} className="p-2 pt-1">
                                        <div className="text-[10px] font-bold text-muted-foreground uppercase mb-1 px-1">
                                          {category.label}
                                        </div>
                                        <div className="flex flex-wrap gap-0.5">
                                          {category.emojis.map((name) => (
                                            <Button
                                              key={name}
                                              variant="ghost"
                                              size="icon"
                                              className={`emoji-font h-8 w-8 text-lg ${msg.reactions?.some((r: any) => r.emoji_name === name && r.user_id === currentUserId) ? "bg-primary/10" : ""}`}
                                              onClick={() => {
                                                const hasReacted = msg.reactions?.some((r: any) => r.emoji_name === name && r.user_id === currentUserId);
                                                handleToggleReaction(msg.id, name, hasReacted);
                                              }}
                                            >
                                              {emojiMap[name]}
                                            </Button>
                                          ))}
                                        </div>
                                      </div>
                                    ))}

                                    {/* Custom Realm Emojis */}
                                    {Object.keys(customEmojis).length > 0 && (
                                      <div className="p-2 pt-1">
                                        <div className="text-[10px] font-bold text-muted-foreground uppercase mb-1 px-1">
                                          Custom
                                        </div>
                                        <div className="flex flex-wrap gap-0.5">
                                          {Object.values(customEmojis).map((emoji: any) => (
                                            <Button
                                              key={emoji.name}
                                              variant="ghost"
                                              size="icon"
                                              className={`h-8 w-8 p-1.5 ${msg.reactions?.some((r: any) => r.emoji_name === emoji.name && r.user_id === currentUserId) ? "bg-primary/10" : ""}`}
                                              onClick={() => {
                                                const hasReacted = msg.reactions?.some((r: any) => r.emoji_name === emoji.name && r.user_id === currentUserId);
                                                handleToggleReaction(msg.id, emoji.name, hasReacted);
                                              }}
                                            >
                                              <img src={emoji.source_url} alt={emoji.name} className="h-full w-full object-contain" title={emoji.name} />
                                            </Button>
                                          ))}
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                </PopoverContent>
                              </Popover>
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })
              )}
            </div>
          </ScrollArea>

          {/* Message Input */}
          <div className="border-t border-border bg-card p-4">
            <div>
              <div className="flex items-center gap-2">
                <Button variant="ghost" size="icon">
                  <Paperclip className="h-5 w-5" />
                </Button>
                <Input
                  placeholder="Type your message..."
                  value={message}
                  onChange={e => setMessage(e.target.value)}
                  onKeyDown={e => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      handleSendMessage();
                    }
                  }}
                  className="flex-1"
                />
                <Button variant="ghost" size="icon">
                  <Smile className="h-5 w-5" />
                </Button>
                <Button onClick={handleSendMessage} disabled={!message.trim()}>
                  <Send className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>
        </main>
      </div>

      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you absolute sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete your
              message from this channel.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDeleteMessage}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default ChannelDetail;
